import React, { useMemo, useState } from 'react';
import BillToLookupField, { type BillToValue } from '../../../components/BillToLookupField';
import CustomerLookupField, { type CustomerValue } from '../../../components/CustomerLookupField';
import DateTimePicker from '../../../components/DateTimePicker';
import SingleSelect from '../../../components/SingleSelect';
import { TanTable } from '../../../components/TanTable';
import service, { type InvoiceLagGroupBy, type InvoiceLagResponse, type InvoiceLagRow } from '../../../services/accountingReports.service';
import { invoiceLagColumns } from './invoiceLagColumns';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reportUtils';

const GROUPS = [
  { value: '', label: '(blank)' }, { value: 'billto', label: 'Bill To' },
  { value: 'customer', label: 'Customer' }, { value: 'invoicedby', label: 'Invoicer' },
  { value: 'division', label: 'Division' },
];
export default function InvoiceLagReport() {
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [billto, setBillto] = useState<BillToValue>({ recno: 0, name: '' });
  const [customer, setCustomer] = useState<CustomerValue>({ code: 0, name: '' });
  const [groupby, setGroupby] = useState('');
  const [result, setResult] = useState<InvoiceLagResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columns = useMemo(() => invoiceLagColumns(result?.mode ?? 'detail', result?.groupBy ?? null), [result?.mode, result?.groupBy]);

  const run = async () => {
    if (end && !start) { setError('Enter a start date when using an end date.'); return; }
    if (start && end && start > end) { setError('End date must be on or after start date.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      setResult(await service.getInvoiceLag({
        ...(start ? { start: dateParam(start) } : {}), ...(end ? { end: dateParam(end) } : {}),
        ...(billto.recno ? { billto: billto.recno } : {}), ...(customer.code ? { customer: customer.code } : {}),
        ...(groupby ? { groupby: groupby as InvoiceLagGroupBy } : {}),
      }));
    } catch (e) { setError(reportError(e, 'Unable to load Invoice Lag. Please try again.')); }
    finally { setLoading(false); }
  };
  return <div className="p-4 space-y-4 text-label">
    <h1 className="text-xl font-semibold">Invoice Lag</h1>
    <div className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 items-start">
        <DateTimePicker mode="date" labelType="custom" label="From Date" ariaLabel="From Date" value={start} onChange={v => setStart(calendarDate(v))} disabled={loading} />
        <DateTimePicker mode="date" labelType="custom" label="To Date" ariaLabel="To Date" value={end} onChange={v => setEnd(calendarDate(v))} disabled={loading} />
        <BillToLookupField value={billto} onChange={setBillto} disabled={loading} />
        <CustomerLookupField value={customer} onChange={setCustomer} disabled={loading} />
        <div><div className="text-sm mb-1">Group By</div><SingleSelect ariaLabel="Group By" options={GROUPS} value={groupby} onChange={setGroupby} disabled={loading} /></div>
      </div>
      <button className={REPORT_BUTTON} onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run Report'}</button>
      <p className="text-sm text-muted">Leave both dates blank to run for not invoiced.</p>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    {result && <p className="text-sm font-medium">{result.criteria}</p>}
    {result?.truncated && <p role="status" className="text-sm text-amber-700 dark:text-amber-300">Showing the first 50,000 rows. Narrow the date range or add a Bill To / Customer filter.</p>}
    {(result || loading) && <div className="h-[65vh] min-h-80">
      <TanTable<InvoiceLagRow> settingsKey="accounting-invoice-lag" data={result?.rows ?? []} columns={columns} loading={loading}
        cardTitle={`Invoice Lag${result ? ` — ${result.criteria}` : ''}`} fillContainer enableColumnFilters />
    </div>}
    {result?.mode === 'grouped' && <p className="text-sm text-muted">Average Invoice Lag is the mean of Master → Invoice days across every row in the group, including rows with no lag (a missing complete or invoice date). This matches the V1 report.</p>}
  </div>;
}
